import React, {
  createContext,
  PropsWithChildren,
  ReactNode,
  useMemo,
  useState,
} from 'react'

export interface ParallaxElementWrapperProps {
  additionalClassName?: string
  scaleFactor?: number
  offset?: number
}

export interface ParallaxElementItem {
  element: ReactNode
  id: string
  props: ParallaxElementWrapperProps
}

export interface ParallaxContextType {
  addParallaxElement: (item: ParallaxElementItem) => void
  removeParallaxElement: (id: string) => void
  updateScaleFactor: (id: string, scaleFactor: number) => void
  updateYPosition: (id: string, position: number) => void
}

const ParallaxElementWrapper = ({
  additionalClassName = '',
  children,
  scaleFactor,
  offset,
}: PropsWithChildren<ParallaxElementWrapperProps>) => {
  const translateY = offset !== undefined ? `${offset}px` : 'var(--tw-translate-y)'

  return (
    <div
      className={`absolute -z-10 backface-hidden w-screen origin-top transform-z ${additionalClassName}`}
      style={{
        transform: `translateY(${translateY}) translateZ(var(--tw-translate-z)) scale(calc(var(--tw-scale-x) + ${
          scaleFactor ?? 0
        }))`,
      }}
    >
      {children}
    </div>
  )
}

// Dummy defaultValue is required here for "gatsby build" to succeed. The "real" initial value is set below though.
export const ParallaxContext = createContext<ParallaxContextType>(
  {} as unknown as ParallaxContextType
)

export const ParallaxContextProvider = ({ children }: PropsWithChildren) => {
  const [parallaxElements, setParallaxElements] = useState<ParallaxElementItem[]>([])

  const value = useMemo<ParallaxContextType>(
    () => ({
      addParallaxElement: item => {
        setParallaxElements(previous => [...previous, item])
      },
      removeParallaxElement: id =>
        setParallaxElements(previous => previous.filter(item => item.id !== id)),
      updateScaleFactor: (id, scaleFactor) =>
        setParallaxElements(previous =>
          previous.reduce<ParallaxElementItem[]>(
            (acc, element) =>
              element.id === id
                ? [
                    ...acc,
                    {
                      ...element,
                      props: {
                        ...element.props,
                        scaleFactor,
                      },
                    },
                  ]
                : [...acc, element],
            []
          )
        ),
      updateYPosition: (id, offset) =>
        setParallaxElements(previous =>
          previous.reduce<ParallaxElementItem[]>(
            (acc, element) =>
              element.id === id
                ? [
                    ...acc,
                    {
                      ...element,
                      props: {
                        ...element.props,
                        offset,
                      },
                    },
                  ]
                : [...acc, element],
            []
          )
        ),
    }),
    []
  )

  return (
    <ParallaxContext.Provider value={value}>
      {parallaxElements.map((item, index) => (
        <ParallaxElementWrapper key={index} {...item.props}>
          {item.element}
        </ParallaxElementWrapper>
      ))}
      {children}
    </ParallaxContext.Provider>
  )
}
